/**
 * Newsletter2Go (Brevo) subscription form of the newsletter tile
 *
 * The tile renders `.n2g-subscription-form` when booost.newsletter2go_form_code
 * is set (tiles/templates/newsletter.pt). This posts the address to the form's
 * submit endpoint the way Newsletter2Go's own utils.js does, without loading
 * that script: Brevo is contacted only when the visitor subscribes
 * (docs/features/NEWSLETTER.md).
 *
 * The answer carries its own status: 201 means the confirmation e-mail of the
 * double opt-in is on its way, 200 means the address was already subscribed -
 * or rejected, when `recipients.invalid` lists it. The messages come from the
 * form's data attributes, translated by the template.
 *
 * The footer arrives through htmx, so the setup runs per fragment.
 */

import onFragment, { within } from './on-fragment'

export default (() => {
  const show = (input, status, ok, message) => {
    input.classList.toggle('is-valid', ok)
    input.classList.toggle('is-invalid', !ok)
    status.classList.toggle('status-success', ok)
    status.classList.toggle('status-error', !ok)
    status.textContent = message
  }

  const outcome = (body) => {
    const code = body && body.status
    if (code === 201) return 'doi'
    if (code === 200) {
      const value = (body.value && body.value[0]) || {}
      const invalid =
        value.result &&
        value.result.error &&
        value.result.error.recipients &&
        value.result.error.recipients.invalid
      return invalid && invalid.length ? 'error' : 'duplicate'
    }
    return 'error'
  }

  const setup = (form) => {
    if (form.dataset.n2gReady) return
    form.dataset.n2gReady = 'true'

    const button = form.querySelector('button[type="submit"]'),
      input = form.querySelector('input[type="email"]'),
      antispam = form.querySelector('.subscription-form-antispam'),
      status = form.querySelector('.subscription-status')

    form.addEventListener('submit', (e) => {
      e.preventDefault()
      if (antispam && antispam.value !== '') return
      if (!input.checkValidity()) {
        show(input, status, false, form.dataset.msgError)
        return
      }
      button.disabled = true
      fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json;charset=utf-8' },
        body: JSON.stringify({ recipient: { email: input.value.trim() } }),
      })
        .then((response) => response.json().catch(() => ({})))
        .then((body) => {
          const result = outcome(body)
          const message = {
            doi: form.dataset.msgDoi,
            duplicate: form.dataset.msgDuplicate,
            error: form.dataset.msgError,
          }[result]
          show(input, status, result !== 'error', message)
          if (result === 'doi') input.value = ''
        })
        .catch(() => show(input, status, false, form.dataset.msgError))
        .finally(() => {
          button.disabled = false
        })
    })
  }

  onFragment((root) => within(root, '.n2g-subscription-form').forEach(setup))
})()
